import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, AlertTriangle, Wind, Activity, Thermometer, Radio, X, Check, ShieldAlert, FileWarning } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface FieldAlert {
  id: string;
  type: "voc" | "air" | "temp" | "humidity" | "compliance";
  severity: "warning" | "critical";
  value: number;
  threshold: number;
  unit: string;
  site: string;
  created_at: string;
  read: boolean;
  dismissed: boolean;
}

const defaultThresholds = {
  voc: { warning: 5, critical: 10, unit: "ppm" },
  air: { warning: 65, critical: 100, unit: "µg/m³" },
  temp: { warning: 95, critical: 110, unit: "°F" },
  humidity: { warning: 90, critical: 95, unit: "% RH" },
};

const iconMap: Record<string, any> = {
  voc: Wind,
  air: Activity,
  temp: Thermometer,
  humidity: Radio,
  compliance: ShieldAlert,
};

const labelMap: Record<string, string> = {
  voc: "VOC",
  air: "PM2.5",
  temp: "Temperature",
  humidity: "Humidity",
  compliance: "Compliance Alert",
};

export default function NotificationCenter() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState<FieldAlert[]>([]);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [thresholds, setThresholds] = useState(defaultThresholds);

  // Load user thresholds
  useEffect(() => {
    if (!user) return;
    const loadThresholds = async () => {
      const { data } = await supabase
        .from("alert_thresholds")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();
      if (data) {
        setThresholds({
          voc: { warning: Number(data.voc_warning), critical: Number(data.voc_critical), unit: "ppm" },
          air: { warning: Number(data.air_warning), critical: Number(data.air_critical), unit: "µg/m³" },
          temp: { warning: Number(data.temp_warning), critical: Number(data.temp_critical), unit: "°F" },
          humidity: { warning: Number(data.humidity_warning), critical: Number(data.humidity_critical), unit: "% RH" },
        });
      }
    };
    loadThresholds();
  }, [user]);

  // Load persisted notifications on mount
  useEffect(() => {
    if (!user) return;
    const loadAlerts = async () => {
      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("dismissed", false)
        .order("created_at", { ascending: false })
        .limit(50);
      if (!error && data) {
        setAlerts(data.map((n: any) => ({
          id: n.id,
          type: n.type,
          severity: n.severity,
          value: Number(n.value),
          threshold: Number(n.threshold),
          unit: n.unit,
          site: n.site,
          created_at: n.created_at,
          read: n.read,
          dismissed: n.dismissed,
        })));
      }
      setLoaded(true);
    };
    loadAlerts();
  }, [user]);

  // Subscribe to realtime inserts
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel("notifications-realtime")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "notifications", filter: `user_id=eq.${user.id}` },
        (payload) => {
          const n = payload.new as any;
          const alert: FieldAlert = {
            id: n.id,
            type: n.type,
            severity: n.severity,
            value: Number(n.value),
            threshold: Number(n.threshold),
            unit: n.unit,
            site: n.site,
            created_at: n.created_at,
            read: n.read,
            dismissed: n.dismissed,
          };
          setAlerts((prev) => [alert, ...prev].slice(0, 50));
          if (alert.type === "compliance") {
            const [docName, ...flagParts] = alert.site.split(" — ");
            const flags = flagParts.join(" — ");
            toast.error(`🛡️ COMPLIANCE: ${alert.value} high-severity flag${alert.value > 1 ? "s" : ""} detected`, {
              description: `${docName}${flags ? ` — ${flags}` : ""}${alert.threshold > 0 ? ` · Risk score: ${alert.threshold}` : ""}`,
              duration: 15000,
            });
          } else if (alert.severity === "critical") {
            toast.error(`🚨 CRITICAL: ${labelMap[alert.type]} at ${alert.value} ${alert.unit}`, {
              description: `${alert.site} — exceeds ${alert.threshold} ${alert.unit} threshold`,
            });
          } else {
            toast.warning(`⚠️ ${labelMap[alert.type]} at ${alert.value} ${alert.unit}`, {
              description: `${alert.site} — above ${alert.threshold} ${alert.unit} warning level`,
            });
          }
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user]);


  const unreadCount = alerts.filter((a) => !a.read).length;

  const markAllRead = async () => {
    setAlerts((prev) => prev.map((a) => ({ ...a, read: true })));
    const unreadIds = alerts.filter((a) => !a.read).map((a) => a.id);
    if (unreadIds.length > 0) {
      await supabase
        .from("notifications")
        .update({ read: true })
        .in("id", unreadIds);
    }
  };

  const dismissAlert = async (id: string) => {
    const snapshot = alerts;
    const removed = alerts.find((a) => a.id === id);
    setAlerts((prev) => prev.filter((a) => a.id !== id));
    const { error } = await supabase
      .from("notifications")
      .update({ dismissed: true })
      .eq("id", id);
    if (error) {
      // Roll back
      setAlerts(snapshot);
      toast.error("Couldn't dismiss alert", { description: error.message });
      return;
    }
    if (removed) {
      toast("Alert dismissed", {
        action: {
          label: "Undo",
          onClick: async () => {
            setAlerts((prev) => [removed, ...prev]);
            await supabase
              .from("notifications")
              .update({ dismissed: false })
              .eq("id", id);
          },
        },
      });
    }
  };

  const markRead = async (id: string) => {
    const snapshot = alerts;
    setAlerts((prev) => prev.map((a) => (a.id === id ? { ...a, read: true } : a)));
    const { error } = await supabase
      .from("notifications")
      .update({ read: true })
      .eq("id", id);
    if (error) {
      setAlerts(snapshot);
      toast.error("Couldn't mark as read", { description: error.message });
    }
  };

  const formatTime = (dateStr: string) =>
    new Date(dateStr).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8 relative">
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 w-4 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold flex items-center justify-center">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="end">
        <div className="flex items-center justify-between p-3 border-b">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">Notifications</h3>
            {unreadCount > 0 && (
              <Badge variant="secondary" className="text-[10px]">
                {unreadCount} new
              </Badge>
            )}
          </div>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={markAllRead}>
              <Check className="h-3 w-3 mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-80">
          {alerts.length === 0 ? (
            <div className="p-8 text-center">
              <Bell className="h-8 w-8 text-muted-foreground mx-auto mb-2 opacity-40" />
              <p className="text-sm text-muted-foreground">No alerts yet</p>
              <p className="text-xs text-muted-foreground mt-1">Threshold breach alerts will appear here</p>
            </div>
          ) : (
            <div className="divide-y">
              {alerts.map((alert) => {
                const Icon = iconMap[alert.type] || AlertTriangle;
                const isCompliance = alert.type === "compliance";
                const [docName, ...flagParts] = alert.site.split(" — ");
                const flagDetail = flagParts.join(" — ");
                return (
                  <div
                    key={alert.id}
                    className={`p-3 flex gap-3 transition-colors cursor-pointer ${
                      !alert.read ? "bg-muted/40" : ""
                    } ${isCompliance && !alert.read ? "bg-destructive/5 border-l-2 border-l-destructive" : ""}`}
                    onClick={() => markRead(alert.id)}
                  >
                    <div
                      className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${
                        isCompliance
                          ? "bg-destructive/20 text-destructive"
                          : alert.severity === "critical"
                            ? "bg-destructive/15 text-destructive"
                            : "bg-warning/15 text-warning"
                      }`}
                    >
                      {isCompliance ? (
                        <ShieldAlert className="h-4 w-4" />
                      ) : alert.severity === "critical" ? (
                        <AlertTriangle className="h-4 w-4" />
                      ) : (
                        <Icon className="h-4 w-4" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      {isCompliance ? (
                        <>
                          <p className="text-sm font-semibold text-destructive">
                            {alert.value} Compliance Flag{alert.value > 1 ? "s" : ""} Detected
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {docName} · {formatTime(alert.created_at)}
                          </p>
                          {flagDetail && (
                            <p className="text-xs text-destructive/80 mt-0.5 line-clamp-2">
                              {flagDetail}
                            </p>
                          )}
                          <div className="flex gap-1 mt-1">
                            <Badge variant="secondary" className="text-[10px] bg-destructive/15 text-destructive">
                              High Severity
                            </Badge>
                            {alert.threshold > 0 && (
                              <Badge variant="secondary" className="text-[10px] bg-muted">
                                Risk: {alert.threshold}
                              </Badge>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <p className="text-sm font-medium">
                            {labelMap[alert.type] || alert.type}: {alert.value} {alert.unit}
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {alert.site} · {formatTime(alert.created_at)}
                          </p>
                          <Badge
                            variant="secondary"
                            className={`text-[10px] mt-1 ${
                              alert.severity === "critical"
                                ? "bg-destructive/15 text-destructive"
                                : "bg-warning/15 text-warning"
                            }`}
                          >
                            {alert.severity === "critical" ? "Critical" : "Warning"} — threshold{" "}
                            {alert.threshold} {alert.unit}
                          </Badge>
                        </>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 shrink-0 opacity-50 hover:opacity-100"
                      onClick={(e) => {
                        e.stopPropagation();
                        dismissAlert(alert.id);
                      }}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
        <div className="p-2 border-t flex flex-col gap-1">
          <Button variant="default" size="sm" className="w-full text-xs h-8" onClick={() => { setOpen(false); navigate("/inbox"); }}>
            Open inbox
          </Button>
          <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => { setOpen(false); navigate("/notifications"); }}>
            View alert history
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
